import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { slideApiService } from '../services/slideApiService';
import { Slide, SearchResult, SearchFilters, BackgroundTask, NamedCollection } from '../types';
import { useShop } from '../contexts/ShopContext';
import { useFavorites } from '../contexts/FavoritesContext';
import SlideCard from './SlideCard';
import CompactSlideCard from './CompactSlideCard';
import SlideEditModal from './SlideEditModal';
import CategoryEditor from './CategoryEditor';
import LocationPlaceEditor from './LocationPlaceEditor';
import CarouselSearchTab from './CarouselSearchTab';
import ClientSlidesBrowse from './ClientSlidesBrowse';
import AddToCollectionModal from './AddToCollectionModal';
import NamePromptModal from './NamePromptModal';

type SlidesSubTab = 'search' | 'carousel';

interface SlidesTabProps {
  shopId: string;
  pendingNewSlide?: { slide: Slide; autoAnalyze?: boolean } | null;
  onPendingNewSlideConsumed?: () => void;
}

const SlidesTab: React.FC<SlidesTabProps> = ({ shopId, pendingNewSlide, onPendingNewSlideConsumed }) => {
  const location = useLocation();
  const [slides, setSlides] = useState<Slide[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [searchFilters, setSearchFilters] = useState<SearchFilters>({ active: true, sold: false, sortBy: 'updatedAt', sortOrder: 'desc' });
  const [editingSlide, setEditingSlide] = useState<Slide | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [categoriesOperator, setCategoriesOperator] = useState<'AND' | 'OR'>('OR');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [includeSold, setIncludeSold] = useState(false);
  const [onlyInactive, setOnlyInactive] = useState(false);
  const [onlySold, setOnlySold] = useState(false);
  const [onlyFavorites, setOnlyFavorites] = useState(false);
  const [filterLocationId, setFilterLocationId] = useState<string | undefined>();
  const [filterPlaceId, setFilterPlaceId] = useState<string | undefined>();

  // "Favorites only" can't be combined with other filters, so it's disabled when any other filter is active.
  const otherFiltersActive = selectedCategories.length > 0 || includeInactive || includeSold || onlyInactive || onlySold || filterLocationId !== undefined || filterPlaceId !== undefined;
  const [viewMode, setViewMode] = useState<'standard' | 'compact'>('standard');
  const [analyzingSlideIds, setAnalyzingSlideIds] = useState<Set<string>>(new Set());
  const analyzePollingRef = useRef<Record<string, NodeJS.Timeout>>({});
  const [activeSubTab, setActiveSubTab] = useState<SlidesSubTab>('search');
  const { isInternalMember } = useShop();
  const { favoriteIds, refresh: refreshFavorites } = useFavorites();

  // Named Collections: actions available when viewing favorites-only.
  const [showSaveAsCollection, setShowSaveAsCollection] = useState(false);
  const [showAddToCollection, setShowAddToCollection] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const showToast = useCallback((message: string) => {
    setToast(message);
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    toastTimeoutRef.current = setTimeout(() => setToast(null), 3000);
  }, []);

  // Save all current favorites as a new collection (server uses the full Redis set,
  // then clears favorites). Mirrors iOS: clears favorites, exits favorites-only.
  const handleSaveFavoritesAsCollection = async (name: string) => {
    const created = await slideApiService.createCollectionFromFavorites(shopId, name);
    setShowSaveAsCollection(false);
    await refreshFavorites();
    setOnlyFavorites(false);
    applyAdvancedFilters();
    showToast(`Saved ${created.slideCount} to "${created.name}"`);
  };

  // Add current favorites to an existing collection. Does NOT clear favorites,
  // so the same set can be added to multiple collections (iOS parity).
  const handleAddFavoritesToCollection = async (collection: NamedCollection) => {
    const ids = Array.from(favoriteIds);
    setShowAddToCollection(false);
    try {
      const updated = await slideApiService.addSlidesToCollection(shopId, collection.id, ids);
      showToast(`Added ${ids.length} to "${updated.name}"`);
    } catch (err) {
      console.error('Failed to add favorites to collection:', err);
      showToast('Failed to add to collection.');
    }
  };


  // Infinite scroll disabled - using explicit pagination controls instead
  const lastSlideElementRef = useCallback((node: HTMLDivElement) => {
    // No-op: pagination controls replace infinite scroll
  }, []);

  const loadSlides = async (pageNum: number = 0, filters: SearchFilters = {}, reset: boolean = true) => {
    try {
      if (reset) {
        setLoading(true);
        setError('');
      } else {
        setLoadingMore(true);
      }

      const pageSize = viewMode === 'compact' ? 60 : 20;
      console.log(`Loading slides for shop ${shopId}, page ${pageNum}, size ${pageSize}, filters:`, filters);

      // Use searchSlides if any filters are provided, otherwise use getSlidesForShop
      const hasFilters = Object.keys(filters).length > 0;
      const result: SearchResult<Slide> = hasFilters
        ? await slideApiService.searchSlides({ ...filters, shopId, page: pageNum, size: pageSize })
        : await slideApiService.getSlidesForShop(shopId, pageNum, pageSize);

      console.log(`Received ${result.items.length} slides, total: ${result.metadata.totalElements}`);

      if (reset) {
        setSlides(result.items);
      } else {
        setSlides(prev => [...prev, ...result.items]);
      }

      setTotalElements(result.metadata.totalElements);
      setHasMore(result.items.length === pageSize);
      setPage(pageNum);
      setError(''); // Clear any previous errors on successful load

      // A favorites-only search self-heals on the server (it drops favorites whose slides were
      // deleted). If the server's favorites count came back smaller than our local set, resync
      // FavoritesContext so the "Save N / Add N" labels and heart state stay in sync.
      if (pageNum === 0 && filters.onlyFavorites && result.metadata.totalElements < favoriteIds.size) {
        refreshFavorites();
      }
    } catch (error) {
      setError('Failed to load slides');
      console.error('Error loading slides:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreSlides = useCallback(() => {
    if (!loadingMore && hasMore) {
      loadSlides(page + 1, searchFilters, false);
    }
  }, [page, searchFilters, loadingMore, hasMore, shopId]);

  useEffect(() => {
    // Internal-only: external (CLIENT/VENDOR) members use the browse view (ClientSlidesBrowse),
    // and slide-svc denies them this search/list path (H4/4b).
    if (isInternalMember) loadSlides(0, searchFilters, true);
  }, [shopId, isInternalMember]);

  // Track current page in a ref so the view mode effect always reads the latest value
  const pageRef = useRef(page);
  pageRef.current = page;

  // When toggling view mode, map the current page to the equivalent page in the new size
  // so the user sees the same slides at the top of the view.
  // Fetches silently without clearing the grid to avoid flicker.
  const prevViewMode = useRef(viewMode);
  useEffect(() => {
    if (prevViewMode.current === viewMode) return;
    const oldSize = prevViewMode.current === 'compact' ? 60 : 20;
    const newSize = viewMode === 'compact' ? 60 : 20;
    const currentPage = pageRef.current;
    const firstItemIndex = currentPage * oldSize;
    const newPage = Math.floor(firstItemIndex / newSize);
    prevViewMode.current = viewMode;

    (async () => {
      try {
        const hasFilters = Object.keys(searchFilters).length > 0;
        const result = hasFilters
          ? await slideApiService.searchSlides({ ...searchFilters, shopId, page: newPage, size: newSize })
          : await slideApiService.getSlidesForShop(shopId, newPage, newSize);
        setSlides(result.items);
        setTotalElements(result.metadata.totalElements);
        setHasMore(result.items.length === newSize);
        setPage(newPage);
      } catch (error) {
        console.error('Error loading slides for view mode switch:', error);
      }
    })();
  }, [viewMode]);

  // Pick up newly created slide from Dashboard-level CreateSlideModal
  useEffect(() => {
    if (pendingNewSlide) {
      setSlides(prev => [pendingNewSlide.slide, ...prev]);
      if (pendingNewSlide.autoAnalyze) {
        setAnalyzingSlideIds(prev => new Set(prev).add(pendingNewSlide.slide.id));
        pollForAnalysisCompletion(pendingNewSlide.slide.id);
      }
      onPendingNewSlideConsumed?.();
    }
  }, [pendingNewSlide]);

  // Handle edit query parameter from Quick Analyze "Save & Edit"
  useEffect(() => {
    // Parse query params from hash (format: #slides?edit=id)
    const hashParts = location.hash.split('?');
    const queryString = hashParts.length > 1 ? hashParts[1] : location.search;
    const params = new URLSearchParams(queryString);
    const editSlideId = params.get('edit');

    if (editSlideId) {
      // Always fetch the slide directly by ID to ensure we have the latest data
      // (newly created slides might not be in the current page/filter results)
      slideApiService.getSlideById(shopId, editSlideId)
        .then(slide => {
          setEditingSlide(slide);
          // Clear the query parameter
          window.history.replaceState({}, '', '/#slides');
        })
        .catch(err => {
          console.error('Failed to load slide for editing:', err);
          // Clear the query parameter even on error
          window.history.replaceState({}, '', '/#slides');
        });
    }
  }, [location.hash, location.search, shopId]);

  const handleSearch = (filters: SearchFilters) => {
    setSearchFilters(filters);
    loadSlides(0, filters, true);
  };

  const applyAdvancedFilters = () => {
    const filters: SearchFilters = {
      ...searchFilters,
      categories: selectedCategories.length > 0 ? selectedCategories : undefined,
      categoriesOperator: selectedCategories.length > 0 ? categoriesOperator : undefined,
      sortBy: 'updatedAt',
      sortOrder: 'desc',
      active: onlyInactive ? false : (includeInactive ? undefined : true),
      sold: onlySold ? true : (includeSold ? undefined : false),
      locationId: filterLocationId,
      placeId: filterPlaceId,
      onlyFavorites: onlyFavorites || undefined,
    };
    handleSearch(filters);
  };

  const clearAdvancedFilters = () => {
    setSelectedCategories([]);
    setCategoriesOperator('OR');
    setIncludeInactive(false);
    setIncludeSold(false);
    setFilterLocationId(undefined);
    setFilterPlaceId(undefined);
    handleSearch({ active: true, sold: false, sortBy: 'updatedAt', sortOrder: 'desc' });
  };

  const clearAllFilters = () => {
    setSelectedCategories([]);
    setCategoriesOperator('OR');
    setIncludeInactive(false);
    setIncludeSold(false);
    setOnlyInactive(false);
    setOnlySold(false);
    setOnlyFavorites(false);
    setFilterLocationId(undefined);
    setFilterPlaceId(undefined);
    handleSearch({ active: true, sold: false, sortBy: 'updatedAt', sortOrder: 'desc' });
  };


  const pollForAnalysisCompletion = (slideId: string) => {
    let attempts = 0;
    const maxAttempts = 30; // 30 x 3s = 90 seconds max
    const intervalMs = 3000;

    const poll = async () => {
      attempts++;
      try {
        const tasks = await slideApiService.getBackgroundTasks(shopId, slideId);
        const activeTask = tasks.find(
          (t: BackgroundTask) => t.taskType === 'AUTO_QUICK_ANALYZE' && (t.status === 'PENDING' || t.status === 'PROCESSING')
        );

        if (!activeTask) {
          // No active task — analysis is done (COMPLETED or FAILED)
          setAnalyzingSlideIds(prev => {
            const next = new Set(prev);
            next.delete(slideId);
            return next;
          });
          delete analyzePollingRef.current[slideId];

          // Refresh the slide data to pick up AI-populated fields
          try {
            const refreshed = await slideApiService.getSlideById(shopId, slideId);
            setSlides(prev => prev.map(s => s.id === slideId ? refreshed : s));
          } catch (refreshErr) {
            console.warn('Failed to refresh slide after analysis:', refreshErr);
          }
          return;
        }

        if (attempts < maxAttempts) {
          analyzePollingRef.current[slideId] = setTimeout(poll, intervalMs);
        } else {
          // Timed out — stop polling
          setAnalyzingSlideIds(prev => {
            const next = new Set(prev);
            next.delete(slideId);
            return next;
          });
          delete analyzePollingRef.current[slideId];
        }
      } catch (err) {
        console.warn('Error polling background tasks:', err);
        if (attempts < maxAttempts) {
          analyzePollingRef.current[slideId] = setTimeout(poll, intervalMs);
        }
      }
    };

    // Start first poll after a short delay (give the CF time to pick up the task)
    analyzePollingRef.current[slideId] = setTimeout(poll, intervalMs);
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      Object.values(analyzePollingRef.current).forEach(timer => clearTimeout(timer));
    };
  }, []);

  const handleSlideUpdated = (updatedSlide: Slide) => {
    setSlides(prev => prev.map(slide =>
      slide.id === updatedSlide.id ? updatedSlide : slide
    ));
  };

  const handleSlideDeleted = (slideId: string) => {
    setSlides(prev => prev.filter(slide => slide.id !== slideId));
  };

  const handleSlideEdit = (slide: Slide) => {
    // Always use the freshest slide data from state to ensure cache busters are current
    const freshSlide = slides.find(s => s.id === slide.id) || slide;
    setEditingSlide(freshSlide);
  };

  const handleEditModalClose = () => {
    setEditingSlide(null);
  };

  // External (CLIENT/VENDOR) members get the read-only shared-slides browse + Carousel search,
  // not the internal search/filter experience (slide-svc denies them slide data — H4/4b).
  if (!isInternalMember) {
    const clientSubTabStyle = (active: boolean): React.CSSProperties => ({
      padding: '10px 20px',
      border: 'none',
      borderBottom: active ? '2px solid var(--primary)' : '2px solid transparent',
      marginBottom: '-2px',
      backgroundColor: 'transparent',
      color: active ? 'var(--text-accent)' : 'var(--text-secondary)',
      fontWeight: active ? 600 : 400,
      cursor: 'pointer',
      fontSize: '0.9375rem',
      transition: 'all 0.15s ease',
    });
    return (
      <div className="slides-tab">
        <div style={{ display: 'flex', gap: '0', marginBottom: '16px', borderBottom: '2px solid var(--border-light)' }}>
          <button onClick={() => setActiveSubTab('search')} style={clientSubTabStyle(activeSubTab === 'search')}>
            Browse
          </button>
          <button onClick={() => setActiveSubTab('carousel')} style={clientSubTabStyle(activeSubTab === 'carousel')}>
            Carousel Search
          </button>
        </div>
        {activeSubTab === 'carousel'
          ? <CarouselSearchTab shopId={shopId} />
          : <ClientSlidesBrowse shopId={shopId} />}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="slides-tab-loading">
        <div className="flex items-center justify-center py-12">
          <div className="spinner-sm mr-3"></div>
          <span className="text-muted">Loading slides...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="slides-tab">
      {/* Sub-tab Navigation: Browse | Carousel Search */}
      <div style={{
        display: 'flex',
        gap: '0',
        marginBottom: '16px',
        borderBottom: '2px solid var(--border-light)',
      }}>
        <button
          onClick={() => setActiveSubTab('search')}
          style={{
            padding: '10px 20px',
            border: 'none',
            borderBottom: activeSubTab === 'search' ? '2px solid var(--primary)' : '2px solid transparent',
            marginBottom: '-2px',
            backgroundColor: 'transparent',
            color: activeSubTab === 'search' ? 'var(--text-accent)' : 'var(--text-secondary)',
            fontWeight: activeSubTab === 'search' ? 600 : 400,
            cursor: 'pointer',
            fontSize: '0.9375rem',
            transition: 'all 0.15s ease',
          }}
        >
          Browse
        </button>
        <button
          onClick={() => setActiveSubTab('carousel')}
          style={{
            padding: '10px 20px',
            border: 'none',
            borderBottom: activeSubTab === 'carousel' ? '2px solid var(--primary)' : '2px solid transparent',
            marginBottom: '-2px',
            backgroundColor: 'transparent',
            color: activeSubTab === 'carousel' ? 'var(--text-accent)' : 'var(--text-secondary)',
            fontWeight: activeSubTab === 'carousel' ? 600 : 400,
            cursor: 'pointer',
            fontSize: '0.9375rem',
            transition: 'all 0.15s ease',
          }}
        >
          Carousel Search
        </button>
      </div>

      {/* Carousel Sub-tab */}
      {activeSubTab === 'carousel' && (
        <CarouselSearchTab shopId={shopId} />
      )}

      {/* Search Sub-tab (existing functionality) */}
      {activeSubTab === 'search' && (<>
      {/* Search and Filter Toolbar */}
      <div className="slides-toolbar card mb-6">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* Deprecated name/description slide search removed; this is now a filtered browse.
              The standard/compact view toggle moved into the filter row below. */}
          {/* Filters — Category, Location/Place, status checkboxes, view toggle, Apply */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-start' }}>
            {/* Category Filter */}
            <div style={{ flex: '1 1 250px', minWidth: '200px', opacity: onlyFavorites ? 0.4 : 1, pointerEvents: onlyFavorites ? 'none' : 'auto' }}>
              <CategoryEditor
                categories={selectedCategories}
                onCategoriesChange={setSelectedCategories}
                shopId={shopId}
                compact
                placeholder="Filter by category..."
              />
              {selectedCategories.length > 0 && (
                <div style={{ marginTop: '6px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--deep-blue)' }}>Match:</span>
                  <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.8125rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      checked={categoriesOperator === 'OR'}
                      onChange={() => setCategoriesOperator('OR')}
                      style={{ marginRight: '4px' }}
                    />
                    Any
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.8125rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      checked={categoriesOperator === 'AND'}
                      onChange={() => setCategoriesOperator('AND')}
                      style={{ marginRight: '4px' }}
                    />
                    All
                  </label>
                </div>
              )}
            </div>

            {/* Location/Place Filter */}
            <div style={{ flex: '1 1 250px', minWidth: '200px', opacity: onlyFavorites ? 0.4 : 1, pointerEvents: onlyFavorites ? 'none' : 'auto' }}>
              <LocationPlaceEditor
                shopId={shopId}
                locationId={filterLocationId}
                placeId={filterPlaceId}
                onLocationChange={setFilterLocationId}
                onPlaceChange={setFilterPlaceId}
                allowCreate={false}
                compact
              />
            </div>

            {/* Status Filters + Actions */}
            <div style={{ flex: '1 1 100%', display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', whiteSpace: 'nowrap', opacity: onlyFavorites ? 0.4 : 1 }}>
                <input
                  type="checkbox"
                  checked={onlyInactive}
                  disabled={onlyFavorites}
                  onChange={(e) => {
                    setOnlyInactive(e.target.checked);
                    if (e.target.checked) setOnlySold(false);
                  }}
                />
                <span style={{ fontWeight: 500, fontSize: '0.8125rem' }}>Only inactive</span>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', whiteSpace: 'nowrap', opacity: onlyFavorites ? 0.4 : 1 }}>
                <input
                  type="checkbox"
                  checked={onlySold}
                  disabled={onlyFavorites}
                  onChange={(e) => {
                    setOnlySold(e.target.checked);
                    if (e.target.checked) setOnlyInactive(false);
                  }}
                />
                <span style={{ fontWeight: 500, fontSize: '0.8125rem' }}>Only sold</span>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', whiteSpace: 'nowrap', opacity: onlyInactive || onlySold || onlyFavorites ? 0.4 : 1 }}>
                <input
                  type="checkbox"
                  checked={includeInactive}
                  onChange={(e) => setIncludeInactive(e.target.checked)}
                  disabled={onlyInactive || onlySold || onlyFavorites}
                />
                <span style={{ fontWeight: 500, fontSize: '0.8125rem' }}>Include inactive</span>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', whiteSpace: 'nowrap', opacity: onlyInactive || onlySold || onlyFavorites ? 0.4 : 1 }}>
                <input
                  type="checkbox"
                  checked={includeSold}
                  onChange={(e) => setIncludeSold(e.target.checked)}
                  disabled={onlyInactive || onlySold || onlyFavorites}
                />
                <span style={{ fontWeight: 500, fontSize: '0.8125rem' }}>Include sold</span>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', whiteSpace: 'nowrap', opacity: otherFiltersActive ? 0.4 : 1 }}>
                <input
                  type="checkbox"
                  checked={onlyFavorites}
                  disabled={otherFiltersActive}
                  onChange={(e) => setOnlyFavorites(e.target.checked)}
                />
                <span style={{ fontWeight: 500, fontSize: '0.8125rem' }}>Favorites only</span>
              </label>

              <button
                onClick={applyAdvancedFilters}
                className="btn btn-primary"
                style={{ padding: '5px 14px', fontSize: '0.8125rem' }}
              >
                Apply
              </button>

              <button
                onClick={clearAllFilters}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-secondary)',
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  padding: '4px',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--text-accent)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-accent)'; }}
                title="Clear all filters"
              >
                Clear
              </button>

              {/* Standard/compact card view toggle — pushed to the far right of the filter row */}
              <div style={{ marginLeft: 'auto', display: 'flex', border: '1px solid var(--border-medium)', borderRadius: '6px', overflow: 'hidden', flex: '0 0 auto' }}>
                <button
                  onClick={() => setViewMode('standard')}
                  title="Standard view"
                  style={{
                    padding: '6px 10px',
                    border: 'none',
                    cursor: 'pointer',
                    backgroundColor: viewMode === 'standard' ? 'var(--delft-blue)' : 'var(--bg-card)',
                    color: viewMode === 'standard' ? 'var(--text-inverse)' : 'var(--text-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="7" height="7" rx="1"/>
                    <rect x="14" y="3" width="7" height="7" rx="1"/>
                    <rect x="3" y="14" width="7" height="7" rx="1"/>
                    <rect x="14" y="14" width="7" height="7" rx="1"/>
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode('compact')}
                  title="Compact view"
                  style={{
                    padding: '6px 10px',
                    border: 'none',
                    borderLeft: '1px solid var(--border-medium)',
                    cursor: 'pointer',
                    backgroundColor: viewMode === 'compact' ? 'var(--delft-blue)' : 'var(--bg-card)',
                    color: viewMode === 'compact' ? 'var(--text-inverse)' : 'var(--text-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="2" width="5" height="5" rx="0.5"/>
                    <rect x="9.5" y="2" width="5" height="5" rx="0.5"/>
                    <rect x="17" y="2" width="5" height="5" rx="0.5"/>
                    <rect x="2" y="9.5" width="5" height="5" rx="0.5"/>
                    <rect x="9.5" y="9.5" width="5" height="5" rx="0.5"/>
                    <rect x="17" y="9.5" width="5" height="5" rx="0.5"/>
                    <rect x="2" y="17" width="5" height="5" rx="0.5"/>
                    <rect x="9.5" y="17" width="5" height="5" rx="0.5"/>
                    <rect x="17" y="17" width="5" height="5" rx="0.5"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-banner card mb-6">
          <div className="text-error">{error}</div>
        </div>
      )}

      {/* Favorites → Collections actions (only while viewing favorites) */}
      {onlyFavorites && favoriteIds.size > 0 && (
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
            padding: '12px 16px', marginBottom: '16px',
            backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-light)', borderRadius: '8px',
          }}
        >
          <span style={{ fontSize: '0.875rem', color: 'var(--text-primary)', fontWeight: 500 }}>
            {favoriteIds.size} {favoriteIds.size === 1 ? 'favorite' : 'favorites'}
          </span>
          <button
            className="btn btn-primary"
            style={{ padding: '5px 14px', fontSize: '0.8125rem' }}
            onClick={() => setShowSaveAsCollection(true)}
          >
            Save as New Collection
          </button>
          <button
            className="btn btn-secondary"
            style={{ padding: '5px 14px', fontSize: '0.8125rem' }}
            onClick={() => setShowAddToCollection(true)}
          >
            Add to Collection
          </button>
        </div>
      )}

      {/* Slides Grid */}
      {slides.length === 0 && !loading ? (
        <div className="empty-state card text-center py-12">
          <h3 className="heading-secondary text-xl mb-2">No Slides Found</h3>
        </div>
      ) : viewMode === 'compact' ? (
        <div className="slides-grid-compact">
          {slides.map((slide, index) => (
            <div
              key={slide.id}
              ref={index === slides.length - 1 ? lastSlideElementRef : null}
            >
              <CompactSlideCard
                slide={slide}
                onSlideUpdated={handleSlideUpdated}
                onSlideDeleted={handleSlideDeleted}
                onSlideEdited={handleSlideEdit}
                isAnalyzing={analyzingSlideIds.has(slide.id)}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="slides-grid">
          {slides.map((slide, index) => (
            <div
              key={slide.id}
              ref={index === slides.length - 1 ? lastSlideElementRef : null}
            >
              <SlideCard
                slide={slide}
                onSlideUpdated={handleSlideUpdated}
                onSlideDeleted={handleSlideDeleted}
                onSlideEdited={handleSlideEdit}
                isAnalyzing={analyzingSlideIds.has(slide.id)}
                onFilterByLocation={(locationId, placeId) => {
                  // Present a clean location view: reset every other filter, keep only location/place.
                  setSelectedCategories([]);
                  setCategoriesOperator('OR');
                  setIncludeInactive(false);
                  setIncludeSold(false);
                  setOnlyInactive(false);
                  setOnlySold(false);
                  setOnlyFavorites(false);
                  setFilterLocationId(locationId);
                  setFilterPlaceId(placeId);
                  handleSearch({
                    active: true,
                    sold: false,
                    locationId,
                    placeId,
                    sortBy: 'updatedAt',
                    sortOrder: 'desc',
                  });
                }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Pagination Controls */}
      {slides.length > 0 && !loading && (
        <div className="pagination-controls" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px',
          padding: '24px 0',
          borderTop: '1px solid var(--border-light)',
          marginTop: '24px'
        }}>
          <p className="text-muted" style={{ fontSize: '0.875rem', margin: 0 }}>
            Showing slides {page * (viewMode === 'compact' ? 60 : 20) + 1}-{Math.min((page + 1) * (viewMode === 'compact' ? 60 : 20), totalElements)} of {totalElements}
          </p>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => {
                if (page > 0) {
                  loadSlides(page - 1, searchFilters, true);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
              disabled={page === 0 || loading}
              className="btn btn-secondary"
              style={{
                opacity: page === 0 || loading ? 0.5 : 1,
                cursor: page === 0 || loading ? 'not-allowed' : 'pointer'
              }}
            >
              ← Previous Page
            </button>

            <button
              onClick={() => {
                if (hasMore) {
                  loadSlides(page + 1, searchFilters, true);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
              disabled={!hasMore || loading}
              className="btn btn-secondary"
              style={{
                opacity: !hasMore || loading ? 0.5 : 1,
                cursor: !hasMore || loading ? 'not-allowed' : 'pointer'
              }}
            >
              Next Page →
            </button>
          </div>
        </div>
      )}

      {/* Loading More Indicator */}
      {loadingMore && (
        <div className="load-more-indicator text-center py-6">
          <div className="spinner-sm mx-auto mb-2"></div>
          <p className="text-muted">Loading...</p>
        </div>
      )}

      {/* Edit Slide Modal */}
      {editingSlide && (
        <SlideEditModal
          slide={editingSlide}
          onClose={handleEditModalClose}
          onSlideUpdated={handleSlideUpdated}
        />
      )}

      {/* Save favorites as a new collection */}
      {showSaveAsCollection && (
        <NamePromptModal
          title="Save as New Collection"
          confirmLabel="Save"
          onCancel={() => setShowSaveAsCollection(false)}
          onSubmit={handleSaveFavoritesAsCollection}
        />
      )}

      {/* Add favorites to an existing collection */}
      {showAddToCollection && (
        <AddToCollectionModal
          shopId={shopId}
          count={favoriteIds.size}
          onPick={handleAddFavoritesToCollection}
          onClose={() => setShowAddToCollection(false)}
        />
      )}

      {/* Transient confirmation toast */}
      {toast && (
        <div
          style={{
            position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
            backgroundColor: 'var(--bg-accent)', color: 'var(--text-primary)', padding: '12px 20px',
            borderRadius: '8px', fontSize: '0.875rem', zIndex: 1100,
            boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
          }}
        >
          {toast}
        </div>
      )}
      </>)}
    </div>
  );
};

export default SlidesTab;